import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { 
  Heart, 
  QrCode, 
  Users, 
  Clock, 
  MapPin,
  AlertTriangle,
  CheckCircle,
  Download,
  Eye,
  Settings,
  Activity
} from 'lucide-react';
import api from '../utils/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();
  const { consentRequests, removeConsentRequest } = useSocket();
  const [profile, setProfile] = useState(null);
  const [scanLogs, setScanLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalScans: 0,
    approvedScans: 0,
    deniedScans: 0,
    pendingRequests: 0
  });

  useEffect(() => {
    fetchProfile();
    fetchScanLogs();
  }, []);

  useEffect(() => {
    setStats(prev => ({
      ...prev,
      pendingRequests: consentRequests.length
    }));
  }, [consentRequests]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile');
      if (response.success) {
        setProfile(response.profile);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchScanLogs = async () => {
    try {
      const response = await api.get('/scan/logs');
      if (response.success) {
        setScanLogs(response.scanLogs);
        const approved = response.scanLogs.filter(log => log.consentStatus === 'approved' || log.consentStatus === 'auto-approved').length;
        const denied = response.scanLogs.filter(log => log.consentStatus === 'denied').length;
        
        setStats(prev => ({
          ...prev,
          totalScans: response.scanLogs.length,
          approvedScans: approved,
          deniedScans: denied
        }));
      }
    } catch (error) {
      console.error('Failed to fetch scan logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConsentResponse = async (requestId, approved) => {
    try {
      const response = await api.post('/scan/respond', {
        scanId: requestId,
        approved
      });

      if (response.success) {
        removeConsentRequest(requestId);
        fetchScanLogs(); // Refresh scan logs
      }
    } catch (error) {
      console.error('Failed to respond to consent:', error);
    }
  };

  const downloadQRCode = () => {
    if (profile?.qrImageUrl) {
      const link = document.createElement('a');
      link.href = profile.qrImageUrl;
      link.download = 'medcard-qr-code.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-600">
            Manage your emergency health profile and monitor access requests
          </p>
        </div>

        {/* Consent Requests Alert */}
        {consentRequests.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-800">
                Emergency Access Requests ({consentRequests.length})
              </h2>
            </div>
            
            <div className="space-y-4">
              {consentRequests.map((request) => (
                <div key={request.requestId} className="bg-white rounded-lg p-4 border border-red-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <div className="flex items-center space-x-2 mb-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {new Date(request.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-800 mb-2">
                        Someone scanned your MedCard at: <strong>{request.location?.address || 'Unknown location'}</strong>
                      </p>
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {request.location?.lat && request.location?.lng 
                            ? `${request.location.lat.toFixed(4)}, ${request.location.lng.toFixed(4)}`
                            : 'Location unknown'
                          }
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleConsentResponse(request.scanId, true)}
                        className="btn-success text-sm px-3 py-1"
                      >
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleConsentResponse(request.scanId, false)}
                        className="btn-danger text-sm px-3 py-1"
                      >
                        <X className="h-4 w-4 inline mr-1" />
                        Deny
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalScans}</p>
                <p className="text-sm text-gray-600">Total Scans</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.approvedScans}</p>
                <p className="text-sm text-gray-600">Approved</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-3 rounded-lg">
                <X className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.deniedScans}</p>
                <p className="text-sm text-gray-600">Denied</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingRequests}</p>
                <p className="text-sm text-gray-600">Pending</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Health Profile</h2>
              
              {profile ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{profile.fullName}</h3>
                      <p className="text-gray-600">Blood Group: {profile.bloodGroup}</p>
                    </div>
                    <div className="text-6xl font-bold text-primary bg-red-50 rounded-xl p-4">
                      {profile.bloodGroup}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Emergency Contacts</h4>
                      <div className="space-y-2">
                        {profile.emergencyContacts.slice(0, 2).map((contact, index) => (
                          <div key={index} className="text-sm">
                            <p className="font-medium">{contact.name} ({contact.relation})</p>
                            <p className="text-gray-600">{contact.phone}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Medical Info</h4>
                      <div className="space-y-1 text-sm">
                        <p>Allergies: {profile.allergies?.length || 0}</p>
                        <p>Conditions: {profile.chronicConditions?.length || 0}</p>
                        <p>Medications: {profile.currentMedications?.length || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4 border-t">
                    <Link to="/profile" className="btn-primary">
                      <Settings className="h-4 w-4 inline mr-2" />
                      Edit Profile
                    </Link>
                    <button
                      onClick={downloadQRCode}
                      className="btn-secondary"
                    >
                      <Download className="h-4 w-4 inline mr-2" />
                      Download QR
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Profile Yet</h3>
                  <p className="text-gray-600 mb-4">
                    Create your emergency health profile to get started
                  </p>
                  <Link to="/profile" className="btn-primary">
                    Create Profile
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
              
              {scanLogs.length > 0 ? (
                <div className="space-y-3">
                  {scanLogs.slice(0, 5).map((log) => (
                    <div key={log._id} className="border-l-4 border-gray-300 pl-3 py-2">
                      <div className="flex items-center space-x-2 mb-1">
                        <Eye className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {log.consentStatus === 'approved' ? 'Access Granted' :
                           log.consentStatus === 'denied' ? 'Access Denied' :
                           log.consentStatus === 'auto-approved' ? 'Auto-Approved' : 'Pending'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {new Date(log.scanTimestamp).toLocaleString()}
                      </p>
                      {log.scannerInfo?.geolocation?.address && (
                        <p className="text-xs text-gray-500">
                          {log.scannerInfo.geolocation.address}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-600">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
